import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRuleUsage } from '../services/api';
import TrustScoreBadge from './TrustScoreBadge';
import RuleHoverCard from './RuleHoverCard';

/**
 * Clickable SCG rule ID that:
 *   - shows a trust score badge inline (from provenance graph)
 *   - opens a rich hover card on hover with rule details + "View in PDF"
 */
export default function SCGRuleLink({ ruleId, className = '', showTrustBadge = true }) {
  const [hovered, setHovered] = useState(false);
  const hoverTimer = useRef(null);

  // Eager fetch — React Query dedupes by ruleId so 50 mounts of the same rule = 1 request.
  const { data: usage } = useQuery({
    queryKey: ['rule-usage', ruleId],
    queryFn: () => getRuleUsage(ruleId),
    enabled: !!ruleId,
    staleTime: 60 * 1000,
    retry: false,
  });

  const onEnter = () => {
    hoverTimer.current = setTimeout(() => {
      setHovered(true);
    }, 200);
  };
  const onLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHovered(false);
  };

  return (
    <span
      className={`relative inline-flex items-center gap-1 ${className}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <span
        className="hover:underline cursor-help"
        style={{ color: 'inherit', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
        title={`Hover for details: ${ruleId}`}
      >
        {ruleId}
      </span>
      {showTrustBadge && usage && usage.cited_count > 0 && (
        <TrustScoreBadge
          trustScore={usage.trust_score}
          citedCount={usage.cited_count}
          overrideCount={usage.override_count}
          size="sm"
        />
      )}
      {hovered && (
        <div
          className="absolute z-50 left-0 top-full mt-1 rounded-lg shadow-xl p-3"
          style={{
            backgroundColor: 'var(--vault-surface, #1e1e2e)',
            border: '1px solid var(--vault-border, #3a3a4a)',
            color: 'var(--text-primary, #e0e0e0)',
            pointerEvents: 'auto',
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <RuleHoverCard ruleId={ruleId} />
        </div>
      )}
    </span>
  );
}
